import type { ExtractedCV } from '../../services/pdf/extractor'
import { useTranslation } from '../../contexts/LanguageContext'

interface PdfReviewModalProps {
  extractedCV: ExtractedCV
  onConfirm: () => void
  onCancel: () => void
  usedAI?: boolean
}

export function PdfReviewModal({
  extractedCV,
  onConfirm,
  onCancel,
  usedAI = false,
}: PdfReviewModalProps) {
  const { t } = useTranslation()
  const { cv } = extractedCV

  return (
    <>
      {/* Fixed header */}
      <div className="flex-shrink-0 px-6 pt-6 pb-4 text-center">
        <div className="w-14 h-14 mx-auto mb-3 bg-emerald-50 rounded-2xl flex items-center justify-center">
          <svg className="w-7 h-7 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <h2 className="font-display text-xl font-bold text-gray-900">
          {t.importReview.processedSuccess}
        </h2>
        <p className="text-sm text-gray-500 mt-1">
          {t.importReview.reviewExtracted}
        </p>

        {usedAI && (
          <div className="flex justify-center mt-3">
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium text-primary bg-primary/10">
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z" />
              </svg>
              {t.importReview.analyzedWithAI}
            </span>
          </div>
        )}
      </div>

      {/* Scrollable data */}
      <div className="flex-1 overflow-y-auto min-h-0 px-6 space-y-4">
        {/* Extracted data card */}
        <div className="rounded-2xl border border-gray-100 divide-y divide-gray-100">
          {/* Personal Info */}
          {cv.personalInfo?.fullName && (
            <div className="p-4">
              <h3 className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-1.5">
                {t.importReview.personalInfo}
              </h3>
              <p className="font-semibold text-gray-900">{cv.personalInfo.fullName}</p>
              {cv.personalInfo.title && (
                <p className="text-sm text-gray-600">{cv.personalInfo.title}</p>
              )}
              {cv.personalInfo.email && (
                <p className="text-sm text-gray-500">{cv.personalInfo.email}</p>
              )}
            </div>
          )}

          {/* Summary */}
          {cv.summary && (
            <div className="p-4">
              <h3 className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-1.5">
                {t.importReview.summary}
              </h3>
              <p className="text-sm text-gray-600 leading-relaxed line-clamp-3">{cv.summary}</p>
            </div>
          )}

          {/* Experience */}
          {cv.experience && cv.experience.length > 0 && (
            <div className="p-4">
              <h3 className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-1.5">
                {t.importReview.experience} ({cv.experience.length})
              </h3>
              <div className="space-y-1.5">
                {cv.experience.slice(0, 2).map((exp, i) => (
                  <div key={i} className="text-sm">
                    <p className="font-medium text-gray-900">{exp.position}</p>
                    <p className="text-gray-500">{exp.company}</p>
                  </div>
                ))}
                {cv.experience.length > 2 && (
                  <p className="text-xs text-gray-400">
                    +{cv.experience.length - 2} {t.importReview.more}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Education */}
          {cv.education && cv.education.length > 0 && (
            <div className="p-4">
              <h3 className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-1.5">
                {t.importReview.education} ({cv.education.length})
              </h3>
              <div className="space-y-1.5">
                {cv.education.slice(0, 2).map((edu, i) => (
                  <div key={i} className="text-sm">
                    <p className="font-medium text-gray-900">{edu.degree}</p>
                    <p className="text-gray-500">{edu.institution}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Languages */}
          {cv.languages && cv.languages.length > 0 && (
            <div className="p-4">
              <h3 className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-1.5">
                {t.importReview.languages}
              </h3>
              <div className="flex flex-wrap gap-1.5">
                {cv.languages.map((lang, i) => (
                  <span
                    key={i}
                    className="inline-flex px-2.5 py-0.5 bg-gray-50 border border-gray-100 rounded-lg text-xs font-medium text-gray-600"
                  >
                    {lang.name}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Info note */}
        <div className="flex items-start gap-2.5 px-4 py-3 bg-gray-50 rounded-xl">
          <svg className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z" />
          </svg>
          <p className="text-xs text-gray-500 leading-relaxed">
            {t.importReview.editableNote}
          </p>
        </div>

        {/* Bottom spacer for scroll content */}
        <div className="h-1" />
      </div>

      {/* Sticky footer */}
      <div className="flex-shrink-0 border-t border-gray-100 px-6 py-4 bg-white rounded-b-2xl">
        <div className="flex gap-3">
          <button
            onClick={onCancel}
            className="flex-1 px-5 py-2.5 text-sm font-semibold text-gray-700 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors"
          >
            {t.common.cancel}
          </button>
          <button
            onClick={onConfirm}
            className="flex-1 px-5 py-2.5 text-sm font-semibold text-white bg-primary rounded-xl hover:opacity-90 transition-opacity"
          >
            {t.importReview.importAndEdit}
          </button>
        </div>
      </div>
    </>
  )
}
